import {InfiniteScroll, WithStyledProps} from '@coolcolduk/ui-mui';
import {uniqueObjectWithKey} from '@coolcolduk/util';
import {Box, CircularProgress, Stack, styled} from '@mui/material';
import {useCallback, useEffect, useState} from 'react';
import {ContactMessage, UserAccount} from '../../__generated__/resolvers-types';
import {useListContactMessages} from '../../hook/useListContactMessages';
import CenteredText from '../centeredText/CenteredText';
import ContactMessageListItem from './ContactMessageListItem';
import ContactMessageDetailsDialog from './ContactMessageDetailsDialog';

const PAGE_SIZE = 20;

export interface ContactMessageListProps {
  user?: UserAccount | null;
}

function ContactMessageListInternal({className, user}: WithStyledProps<ContactMessageListProps>) {
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  const {data, loading, error} = useListContactMessages({
    limit: PAGE_SIZE,
    offset,
    user,
  });

  const listContactMessages = data?.listContactMessages;

  useEffect(() => {
    if (listContactMessages) {
      setMessages((prev) => {
        const newMessages = uniqueObjectWithKey([...prev, ...listContactMessages], 'id');
        return newMessages;
      });
      setHasMore(listContactMessages.length === PAGE_SIZE);
    }
  }, [listContactMessages]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      setOffset((prev) => prev + PAGE_SIZE);
    }
  }, [loading, hasMore]);

  const handleMessageClick = useCallback((message: ContactMessage) => {
    setSelectedMessage(message);
    setDetailsDialogOpen(true);
  }, []);

  const handleCloseDetailsDialog = useCallback(() => {
    setDetailsDialogOpen(false);
    setSelectedMessage(null);
  }, []);

  if (error) {
    return <CenteredText text={`Error loading messages: ${error.message}`} minHeight={300} />;
  }

  if (!loading && messages.length === 0) {
    return <CenteredText text="No contact messages available" minHeight={300} />;
  }

  return (
    <>
      <InfiniteScroll
        className={className}
        dataLength={messages.length}
        next={loadMore}
        hasMore={hasMore}
        loader={
          <Box p={2} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        }
        scrollThreshold={0.8}
        uniqueKey="contact-message-list"
        height={600}
      >
        <Stack spacing={2}>
          {messages.map((message) => (
            <ContactMessageListItem key={message.id} message={message} onClick={handleMessageClick} />
          ))}
        </Stack>
      </InfiniteScroll>

      <ContactMessageDetailsDialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        message={selectedMessage}
      />
    </>
  );
}

export default styled(ContactMessageListInternal, {name: 'CcContactMessageList'})<ContactMessageListProps>();
